/**
 * account.tsx — the account window (§7.15), one component for both apps.
 * The terminal and the site each pass their own data + callbacks; nothing in
 * here talks to a server. Two columns: identity + track record on the left,
 * linked accounts / security / exchange API on the right.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, HoldButton, IconButton, Input, Segmented, Spinner, Toggle } from './components';
import { IconActivity, IconKey, IconSignOut, IconUser } from './icons';
import { tr } from './i18n';

export interface AccountStats { count: number; wins: number; losses: number; winRate: number; avgPnlPct: number }
export interface LinkedAccount { kind: 'email' | 'telegram'; value: string | null; primary: boolean; onAction?: () => void; actionLabel?: string; busy?: boolean }
export interface VenueKeyState {
  id: string; label: string; sub: string; status: 'active' | 'off' | 'soon'; needsPassphrase: boolean; hasTestnet: boolean;
  /** Where this venue's key is stored (T03) — one line each on the card. */
  where?: KeyWhere[];
  /** Shown above the key fields, before "Connect & verify" (e.g. the site's server-IP step). */
  note?: string;
  /** The terminal (owner 10.10.2026): offer "This PC only" / "This PC + server" in the form. */
  canServer?: boolean;
  /** A line under the card when one storage place failed (e.g. the server refused the key). */
  warn?: string;
  /** The saved API key, masked (`abcd••••wxyz`) — shown in the fields on Edit so it is clear a key is there. */
  keyHint?: string;
  /** Owner 11.10.2026: a TESTNET key has its own slot (the terminal keeps it on
   * our server as a bot key) and never replaces the live one. Its mask, when saved. */
  testnetHint?: string;
  /** The testnet slot exists for this venue (the toggle then switches slots). */
  separateTestnet?: boolean;
}
export type KeyWhere = 'local' | 'server';

/** The API server's public IP: every site / bot call reaches the exchange from here. */
export const SERVER_IP = '94.130.225.155';

export const keyWhereLabel = (w: KeyWhere): string =>
  w === 'local' ? tr('This computer (terminal, your IP)') : tr('Server (site & bots, IP {ip})', { ip: SERVER_IP });

/** The site's step before a server-side key check (Binance answers -2015 to a key restricted to another IP). */
export const serverIpNote = (venue: string): string =>
  tr('Add {ip} to this key’s trusted IPs on {v} first — the site and bots trade from our server. Keys used only in the terminal trade from YOUR IP.', { ip: SERVER_IP, v: venue });
export interface VenueKeys {
  apiKey: string; apiSecret: string; passphrase?: string; testnet?: boolean;
  /** Only with `canServer`: 'server' = this PC AND encrypted on our server (site trading, journal sync, 24/7 stops). */
  store?: 'local' | 'server';
}
export interface AccountWindowProps {
  me: { id: string; nickname: string; role: string; avatar: { kind: string | null; id: string | null; url: string | null } };
  /** `paper`: the shared paper account (/virtual, T04) — its own tab when given. */
  stats: { account: AccountStats | null; posted: AccountStats | null; paper?: AccountStats | null; loading?: boolean };
  linked: LinkedAccount[];
  venues: VenueKeyState[];
  /** Throws Error(message) on failure — the message is shown under the fields. */
  onConnectVenue: (id: string, keys: VenueKeys) => Promise<void>;
  onForgetVenue?: (id: string) => Promise<void>;
  security?: { passwordSet: boolean; onSet: () => void; onChange: () => void; onRemove: () => void; /** greyed out, with `note` as the subtitle */ disabled?: boolean; note?: string } | null;
  onOpenJournal: () => void;
  onSignOut: () => void;
  onClose: () => void;
  initialTab?: 'linked' | 'api';
  /** Site-only extras: edit the identity in place, and one extra footer slot. */
  onPickPreset?: (id: string) => void;
  onUploadAvatar?: (file: File) => void;
  onRename?: (nickname: string) => void;
  onAdmin?: () => void;
  footerExtra?: ReactNode;
}

/** Hues only — the glyph and geometry match on both apps. */
export const PRESET_AVATARS: Record<string, { hue1: number; hue2: number; glyph: string }> = {
  mint: { hue1: 162, hue2: 200, glyph: '◇' },
  amber: { hue1: 38, hue2: 18, glyph: '▲' },
  azure: { hue1: 215, hue2: 250, glyph: '○' },
  rose: { hue1: 348, hue2: 320, glyph: '●' },
  iris: { hue1: 270, hue2: 305, glyph: '▰' },
};

export function AccountAvatar({ avatar, size = 112 }: { avatar: AccountWindowProps['me']['avatar']; size?: number }) {
  const glow = { boxShadow: '0 0 40px -8px color-mix(in srgb, var(--brand) 55%, transparent)' };
  if (avatar?.kind === 'image' && avatar.url) {
    return <div className="qt-acct__avatar" style={{ width: size, height: size, background: `url(${avatar.url}) center/cover no-repeat`, ...glow }} />;
  }
  const p = PRESET_AVATARS[avatar?.id || 'mint'] || PRESET_AVATARS.mint;
  return (
    <div className="qt-acct__avatar" style={{ width: size, height: size, fontSize: size * 0.42, ...glow,
      background: `linear-gradient(135deg, hsla(${p.hue1},60%,55%,0.55), hsla(${p.hue2},65%,45%,0.55))` }}>
      {p.glyph}
    </div>
  );
}

/** Paper track record from the shared paper account's closed trades (/virtual state.closed). */
export function statsFromPaper(closed: { pnl: number; entry: number; qty: number }[] | null | undefined): AccountStats | null {
  return statsFromTrades((closed ?? []).map((c) => ({ pnlUsd: c.pnl, pnlPct: c.entry > 0 && c.qty > 0 ? (c.pnl / (c.entry * c.qty)) * 100 : 0 })));
}

/** Track-record numbers from closed-trade rows (`/trades` on the server). */
export function statsFromTrades(rows: { pnlUsd: number; pnlPct: number }[]): AccountStats | null {
  if (!rows.length) return null;
  const wins = rows.filter((r) => r.pnlUsd > 0).length;
  const losses = rows.filter((r) => r.pnlUsd < 0).length;
  return { count: rows.length, wins, losses, winRate: (wins / rows.length) * 100, avgPnlPct: rows.reduce((s, r) => s + r.pnlPct, 0) / rows.length };
}

const signed = (n: number, d = 2) => `${n >= 0 ? '+' : ''}${n.toFixed(d)}%`;

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'bull' | 'bear' }) {
  return (
    <div className="qt-acct__tile">
      <span className="qt-acct__tile-v num" style={tone ? { color: `var(--t-num-${tone})` } : undefined}>{value}</span>
      <span className="qt-acct__tile-l">{label}</span>
    </div>
  );
}

/** Distinct marks: two-letter prefixes made Binance and Bitget both read `BI`. */
const VENUE_MARK: Record<string, string> = { binance: 'BNB', bybit: 'BYB', okx: 'OKX', bitget: 'BGT' };

function VenueCard({ v, onConnect, onForget }: { v: VenueKeyState; onConnect: AccountWindowProps['onConnectVenue']; onForget?: AccountWindowProps['onForgetVenue'] }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [pass, setPass] = useState('');
  // Live by default: the testnet toggle switches to the separate testnet slot.
  const [testnet, setTestnet] = useState(false);
  const [store, setStore] = useState<'local' | 'server'>(v.where?.includes('server') ? 'server' : 'local');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Owner 10.10.2026: on Edit, the saved key shows IN the fields (masked) until
  // the user starts typing a new one — an empty form read as "nothing saved".
  const [typing, setTyping] = useState(false);
  const testSlot = !!v.separateTestnet && testnet;
  const slotHint = testSlot ? v.testnetHint : v.keyHint;
  const saved = !!slotHint && (testSlot || v.status === 'active') && !typing;
  const soon = v.status === 'soon';
  const collapse = () => { setOpen(false); setErr(null); setKey(''); setSecret(''); setPass(''); setTyping(false); };
  const startTyping = () => { if (!typing) { setTyping(true); setKey(''); setSecret(''); setPass(''); } };
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onConnect(v.id, { apiKey: key.trim(), apiSecret: secret.trim(), passphrase: v.needsPassphrase ? pass : undefined, testnet: v.hasTestnet ? testnet : undefined, store: v.canServer ? store : undefined });
      collapse();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  return (
    <div className="qt-acct__card" data-active={v.status === 'active' || undefined}>
      <div className="qt-acct__cardrow">
        <div className="qt-acct__logo num">{VENUE_MARK[v.id] ?? v.label.slice(0, 3).toUpperCase()}</div>
        <div className="qt-acct__grow">
          <div className="qt-acct__name">{v.label}</div><div className="qt-acct__sub">{v.sub}</div>
          {v.where?.map((w) => <div key={w} className="qt-acct__sub">{tr('Key')}: {keyWhereLabel(w)}</div>)}
          {v.keyHint && v.status === 'active' && <div className="qt-acct__sub num">{tr('API key')}: {v.keyHint}</div>}
          {v.testnetHint && <div className="qt-acct__sub num">{tr('Testnet key (bots)')}: {v.testnetHint}</div>}
          {v.warn && <div className="qt-acct__warn">{v.warn}</div>}
        </div>
        {soon ? <Badge tone="neutral">{tr('Soon')}</Badge>
          : <Badge tone={v.status === 'active' ? 'bull' : 'neutral'} dot>{v.status === 'active' ? tr('Active') : tr('Not connected')}</Badge>}
        {!soon && (
          <>
            {v.status === 'active' && onForget && <Button size="sm" variant="ghost" onClick={() => { void onForget(v.id); }}>{tr('Disconnect')}</Button>}
            <Button size="sm" variant="secondary" onClick={() => (open ? collapse() : setOpen(true))}>{open ? tr('Cancel') : v.status === 'active' ? tr('Edit') : tr('Connect +')}</Button>
          </>
        )}
      </div>
      <div className="qt-acct__expand" data-open={open || undefined}>
        <div className="qt-acct__expand-in">
          <form className="qt-acct__form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            {v.canServer && !testSlot && (
              <Segmented label={tr('Store the key')} value={store} onChange={setStore} options={[
                { value: 'local', label: tr('This PC only') },
                { value: 'server', label: tr('This PC + server') },
              ]} />
            )}
            {testSlot && <div className="qt-acct__sub">{tr('Testnet keys have their own slot: stored on our server for bots on the testnet, your live key stays as it is.')}</div>}
            {v.canServer && !testSlot && <div className="qt-acct__sub">{store === 'local'
              ? tr('Trades from your IP while the terminal runs. The site shows your journal after you sync it here; trading from the site asks to move the key to the server first.')
              : tr('Also encrypted on our server: trading and journal on the site, stops and trailing kept 24/7 with the terminal closed.')}</div>}
            {!testSlot && (v.note || (v.canServer && store === 'server')) && <div className="qt-acct__warn">{v.note ?? serverIpNote(v.label)}</div>}
            {saved && <div className="qt-acct__sub">{tr('Saved keys are below. Type over them to replace.')}</div>}
            <Input mono placeholder={tr('API key')} value={saved ? slotHint ?? '' : key} onFocus={startTyping} onChange={(e) => { startTyping(); setKey(e.target.value); }} autoComplete="off" />
            <Input mono placeholder={tr('API secret')} type="password" value={saved ? '••••••••••••••••' : secret} onFocus={startTyping} onChange={(e) => { startTyping(); setSecret(e.target.value); }} autoComplete="off" />
            {v.needsPassphrase && <Input mono placeholder={tr('API passphrase')} type="password" value={saved ? '••••••••' : pass} onFocus={startTyping} onChange={(e) => { startTyping(); setPass(e.target.value); }} autoComplete="off" />}
            <div className="qt-acct__formrow">
              {v.hasTestnet && <label className="qt-acct__inline"><Toggle checked={testnet} onChange={(on) => { setTestnet(on); setTyping(false); setKey(''); setSecret(''); setPass(''); }} label={tr('testnet / demo')} /> {tr('testnet / demo')}</label>}
              <Badge tone="neutral">{tr('Read + Trade · no withdrawals')}</Badge>
              <span className="qt-acct__grow" />
              <IconButton type="button" label={tr('Cancel')} onClick={collapse}>✕</IconButton>
              <Button type="submit" variant="primary" size="sm" loading={busy} disabled={saved || !key || !secret || (v.needsPassphrase && !pass)}>{tr('Connect & verify')}</Button>
            </div>
            {v.hasTestnet && !testnet && <div className="qt-acct__warn">{tr('These keys will trade a REAL account.')}</div>}
            {err && <div className="qt-acct__err">{err}</div>}
          </form>
        </div>
      </div>
    </div>
  );
}

export function AccountWindow(p: AccountWindowProps) {
  const [tab, setTab] = useState<'linked' | 'api'>(p.initialTab ?? 'linked');
  const [mode, setMode] = useState<'account' | 'posted' | 'paper'>('account');
  const [editing, setEditing] = useState(false);
  const [nick, setNick] = useState(p.me.nickname);
  const fileRef = useRef<HTMLInputElement>(null);
  const closeRef = useRef(p.onClose);
  closeRef.current = p.onClose;
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);
  const st = mode === 'account' ? p.stats.account : mode === 'paper' ? p.stats.paper ?? null : p.stats.posted;
  const isAdmin = p.me.role === 'ADMIN';
  const logoOf = (k: LinkedAccount['kind']) => (k === 'email' ? '@' : '✈');

  return createPortal(
    <div className="qt-scrim" onMouseDown={(e) => e.target === e.currentTarget && p.onClose()}>
      <div className="qt-acct" role="dialog" aria-modal="true" aria-label={tr('Account')}>
        <aside className="qt-acct__left">
          <div className="qt-acct__id">
            <div style={{ position: 'relative' }}>
              <AccountAvatar avatar={p.me.avatar} />
              {p.onUploadAvatar && (
                <>
                  <IconButton label={tr('Upload avatar')} className="qt-acct__upl" onClick={() => fileRef.current?.click()}>+</IconButton>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) p.onUploadAvatar!(f); }} />
                </>
              )}
            </div>
            {p.onPickPreset && (
              <div className="qt-acct__presets">
                {Object.entries(PRESET_AVATARS).map(([id, a]) => (
                  <button key={id} type="button" aria-label={id} aria-pressed={p.me.avatar.kind === 'preset' && p.me.avatar.id === id}
                    onClick={() => p.onPickPreset!(id)} style={{ background: `linear-gradient(135deg, hsla(${a.hue1},60%,55%,.55), hsla(${a.hue2},65%,45%,.55))` }}>{a.glyph}</button>
                ))}
              </div>
            )}
            {editing ? (
              <form className="qt-acct__formrow" onSubmit={(e) => { e.preventDefault(); p.onRename?.(nick.trim() || p.me.nickname); setEditing(false); }}>
                <Input value={nick} onChange={(e) => setNick(e.target.value)} autoFocus />
                <Button size="sm" variant="primary" type="submit">{tr('Save')}</Button>
              </form>
            ) : (
              <div className="qt-acct__nick">
                <span>{p.me.nickname}</span>
                {isAdmin && <span onClick={p.onAdmin} style={{ cursor: p.onAdmin ? 'pointer' : undefined }}><Badge tone="warn" caps>{tr('ADMIN')}</Badge></span>}
                {p.onRename && <IconButton label={tr('Edit nickname')} onClick={() => { setNick(p.me.nickname); setEditing(true); }}>✎</IconButton>}
              </div>
            )}
            <span className="qt-acct__uid num">ID {p.me.id}</span>
          </div>

          <div className="qt-acct__rec">
            <div className="qt-acct__rec-hd"><IconActivity size={14} /> {tr('Trading track record')}</div>
            <Segmented label={tr('Track record')} value={mode} onChange={setMode} options={[{ value: 'account', label: tr('Account') }, ...(p.stats.paper !== undefined ? [{ value: 'paper' as const, label: tr('Paper') }] : []), { value: 'posted', label: tr('Posted') }]} />
            {p.stats.loading ? <div className="qt-acct__empty"><Spinner /></div>
              : st && st.count > 0 ? (
                <div className="qt-acct__tiles">
                  <Tile label={tr('TRADES')} value={String(st.count)} />
                  <Tile label={tr('WIN RATE')} value={`${st.winRate.toFixed(0)}%`} tone={st.winRate >= 50 ? 'bull' : 'bear'} />
                  <Tile label={tr('AVG PNL')} value={signed(st.avgPnlPct)} tone={st.avgPnlPct >= 0 ? 'bull' : 'bear'} />
                  <Tile label="W / L" value={`${st.wins} / ${st.losses}`} />
                </div>
              ) : <div className="qt-acct__empty">{mode === 'posted' ? tr('No closed situations yet') : tr('No closed trades yet')}</div>}
          </div>

          <Button variant="secondary" style={{ width: '100%' }} onClick={p.onOpenJournal}>{tr('Open trading journal')}</Button>
          <span className="qt-acct__grow" />
          <HoldButton variant="ghost" onConfirm={p.onSignOut} icon={<IconSignOut size={14} />} style={{ color: 'var(--bear)' }}>{tr('Sign out')}</HoldButton>
        </aside>

        <section className="qt-acct__right">
          <header className="qt-acct__hd">
            <h2>{tab === 'linked' ? tr('Linked accounts & security') : tr('Exchange API')}</h2>
            <span className="qt-acct__grow" />
            <Button size="sm" variant={tab === 'linked' ? 'secondary' : 'ghost'} icon={<IconUser size={14} />} onClick={() => setTab('linked')}>{tr('Security')}</Button>
            <Button size="sm" variant={tab === 'api' ? 'secondary' : 'ghost'} icon={<IconKey size={14} />} onClick={() => setTab('api')}>{tr('Exchange API')}</Button>
          </header>
          <p className="qt-acct__subtitle">{tab === 'linked' ? tr('The ways you can sign in to Quantum.') : tr('Keys are checked with a read-only call before they are saved. Withdrawals are never enabled.')}</p>

          <div className="qt-acct__scroll">
            {tab === 'linked' ? (
              <>
                {p.linked.map((l) => (
                  <div key={l.kind} className="qt-acct__card" data-active={l.primary || undefined}>
                    <div className="qt-acct__cardrow">
                      <div className="qt-acct__logo">{logoOf(l.kind)}</div>
                      <div className="qt-acct__grow">
                        <div className="qt-acct__name">{l.kind === 'email' ? tr('Email') : 'Telegram'}</div>
                        <div className="qt-acct__sub">{l.value ?? tr('Not connected')}</div>
                      </div>
                      <Badge tone={l.value ? 'bull' : 'neutral'} dot>{l.value ? (l.primary ? tr('Active') : tr('Connected')) : tr('Not connected')}</Badge>
                      {l.onAction && <Button size="sm" variant="secondary" loading={l.busy} onClick={l.onAction}>{l.actionLabel ?? (l.value ? tr('Edit') : tr('Connect +'))}</Button>}
                    </div>
                  </div>
                ))}
                {p.security && (
                  <div className="qt-acct__card">
                    <div className="qt-acct__cardrow">
                      <div className="qt-acct__logo"><IconKey size={18} /></div>
                      <div className="qt-acct__grow">
                        <div className="qt-acct__name">{tr('Terminal password')}</div>
                        <div className="qt-acct__sub">{p.security.note ?? (p.security.passwordSet ? tr('Asked on every start of the terminal') : tr('Protect the terminal with a password on start'))}</div>
                      </div>
                      <Badge tone={p.security.passwordSet ? 'bull' : 'neutral'} dot>{p.security.passwordSet ? tr('Active') : tr('Off')}</Badge>
                      {p.security.passwordSet ? (
                        <>
                          <Button size="sm" variant="secondary" onClick={p.security.onChange}>{tr('Change')}</Button>
                          <Button size="sm" variant="ghost" onClick={p.security.onRemove}>{tr('Remove')}</Button>
                        </>
                      ) : <Button size="sm" variant="secondary" disabled={p.security.disabled} onClick={p.security.onSet}>{tr('Set password')}</Button>}
                    </div>
                  </div>
                )}
              </>
            ) : p.venues.map((v) => <VenueCard key={v.id} v={v} onConnect={p.onConnectVenue} onForget={p.onForgetVenue} />)}
          </div>

          <footer className="qt-acct__ft">
            {p.footerExtra}
            <span className="qt-acct__grow" />
            <Button variant="primary" onClick={p.onClose}>{tr('Save & close')}</Button>
          </footer>
        </section>
      </div>
    </div>,
    document.body,
  );
}
